// Client-side PSS checksum helpers for browser-direct login.
//
// Ported from functions/_shared/pss-utils.js so the browser can sign PSS API
// requests itself and call the PSS API directly — making each login egress from
// the user's OWN IP instead of Cloudflare's shared egress IPs (which collectively
// trip PSS's per-IP "too many accounts created in 24 hours" device-creation cap).
//
// NOTE: CHECKSUM_KEY is NOT a secret — it is the same constant baked into the
// public PSS game client and is widely known in the PSS community. Embedding it
// here leaks nothing that isn't already public.

export const CHECKSUM_KEY = '5343Savvy!s0d@';
export const DEVICE_TYPE = 'DeviceTypeWeb';

// ---------------------------------------------------------------------------
// Pure-JS MD5 (no Web Crypto — md5 isn't available in SubtleCrypto).
// Input is UTF-8 encoded before processing. Identical to the Worker's md5().
// ---------------------------------------------------------------------------
function safeAdd(x: number, y: number): number {
  const lsw = (x & 0xffff) + (y & 0xffff);
  const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
  return (msw << 16) | (lsw & 0xffff);
}
function bitRotateLeft(num: number, cnt: number): number {
  return (num << cnt) | (num >>> (32 - cnt));
}
function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, (b & c) | (~b & d)), safeAdd(x, t)), s), b);
}
function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, (b & d) | (c & ~d)), safeAdd(x, t)), s), b);
}
function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, b ^ c ^ d), safeAdd(x, t)), s), b);
}
function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
  return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, c ^ (b | ~d)), safeAdd(x, t)), s), b);
}
function str2binl(str: string): number[] {
  const bin: number[] = [];
  const mask = (1 << 8) - 1;
  for (let i = 0; i < str.length * 8; i += 8) {
    bin[i >> 5] |= (str.charCodeAt(i / 8) & mask) << (i % 32);
  }
  return bin;
}
function binl2hex(binarray: number[]): string {
  const hexTab = '0123456789abcdef';
  let str = '';
  for (let i = 0; i < binarray.length * 4; i++) {
    str += hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8 + 4)) & 0xf) +
           hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8))     & 0xf);
  }
  return str;
}
function binlMD5(x: number[], len: number): number[] {
  x[len >> 5] |= 0x80 << (len % 32);
  x[(((len + 64) >>> 9) << 4) + 14] = len;
  let a =  1732584193;
  let b = -271733879;
  let c = -1732584194;
  let d =  271733878;
  for (let i = 0; i < x.length; i += 16) {
    const olda = a, oldb = b, oldc = c, oldd = d;
    a = md5ff(a,b,c,d, x[i+ 0], 7 , -680876936);
    d = md5ff(d,a,b,c, x[i+ 1], 12, -389564586);
    c = md5ff(c,d,a,b, x[i+ 2], 17,  606105819);
    b = md5ff(b,c,d,a, x[i+ 3], 22, -1044525330);
    a = md5ff(a,b,c,d, x[i+ 4], 7 , -176418897);
    d = md5ff(d,a,b,c, x[i+ 5], 12,  1200080426);
    c = md5ff(c,d,a,b, x[i+ 6], 17, -1473231341);
    b = md5ff(b,c,d,a, x[i+ 7], 22, -45705983);
    a = md5ff(a,b,c,d, x[i+ 8], 7 ,  1770035416);
    d = md5ff(d,a,b,c, x[i+ 9], 12, -1958414417);
    c = md5ff(c,d,a,b, x[i+10], 17, -42063);
    b = md5ff(b,c,d,a, x[i+11], 22, -1990404162);
    a = md5ff(a,b,c,d, x[i+12], 7 ,  1804603682);
    d = md5ff(d,a,b,c, x[i+13], 12, -40341101);
    c = md5ff(c,d,a,b, x[i+14], 17, -1502002290);
    b = md5ff(b,c,d,a, x[i+15], 22,  1236535329);
    a = md5gg(a,b,c,d, x[i+ 1], 5 , -165796510);
    d = md5gg(d,a,b,c, x[i+ 6], 9 , -1069501632);
    c = md5gg(c,d,a,b, x[i+11], 14,  643717713);
    b = md5gg(b,c,d,a, x[i+ 0], 20, -373897302);
    a = md5gg(a,b,c,d, x[i+ 5], 5 , -701558691);
    d = md5gg(d,a,b,c, x[i+10], 9 ,  38016083);
    c = md5gg(c,d,a,b, x[i+15], 14, -660478335);
    b = md5gg(b,c,d,a, x[i+ 4], 20, -405537848);
    a = md5gg(a,b,c,d, x[i+ 9], 5 ,  568446438);
    d = md5gg(d,a,b,c, x[i+14], 9 , -1019803690);
    c = md5gg(c,d,a,b, x[i+ 3], 14, -187363961);
    b = md5gg(b,c,d,a, x[i+ 8], 20,  1163531501);
    a = md5gg(a,b,c,d, x[i+13], 5 , -1444681467);
    d = md5gg(d,a,b,c, x[i+ 2], 9 , -51403784);
    c = md5gg(c,d,a,b, x[i+ 7], 14,  1735328473);
    b = md5gg(b,c,d,a, x[i+12], 20, -1926607734);
    a = md5hh(a,b,c,d, x[i+ 5], 4 , -378558);
    d = md5hh(d,a,b,c, x[i+ 8], 11, -2022574463);
    c = md5hh(c,d,a,b, x[i+11], 16,  1839030562);
    b = md5hh(b,c,d,a, x[i+14], 23, -35309556);
    a = md5hh(a,b,c,d, x[i+ 1], 4 , -1530992060);
    d = md5hh(d,a,b,c, x[i+ 4], 11,  1272893353);
    c = md5hh(c,d,a,b, x[i+ 7], 16, -155497632);
    b = md5hh(b,c,d,a, x[i+10], 23, -1094730640);
    a = md5hh(a,b,c,d, x[i+13], 4 ,  681279174);
    d = md5hh(d,a,b,c, x[i+ 0], 11, -358537222);
    c = md5hh(c,d,a,b, x[i+ 3], 16, -722521979);
    b = md5hh(b,c,d,a, x[i+ 6], 23,  76029189);
    a = md5hh(a,b,c,d, x[i+ 9], 4 , -640364487);
    d = md5hh(d,a,b,c, x[i+12], 11, -421815835);
    c = md5hh(c,d,a,b, x[i+15], 16,  530742520);
    b = md5hh(b,c,d,a, x[i+ 2], 23, -995338651);
    a = md5ii(a,b,c,d, x[i+ 0], 6 , -198630844);
    d = md5ii(d,a,b,c, x[i+ 7], 10,  1126891415);
    c = md5ii(c,d,a,b, x[i+14], 15, -1416354905);
    b = md5ii(b,c,d,a, x[i+ 5], 21, -57434055);
    a = md5ii(a,b,c,d, x[i+12], 6 ,  1700485571);
    d = md5ii(d,a,b,c, x[i+ 3], 10, -1894986606);
    c = md5ii(c,d,a,b, x[i+10], 15, -1051523);
    b = md5ii(b,c,d,a, x[i+ 1], 21, -2054922799);
    a = md5ii(a,b,c,d, x[i+ 8], 6 ,  1873313359);
    d = md5ii(d,a,b,c, x[i+15], 10, -30611744);
    c = md5ii(c,d,a,b, x[i+ 6], 15, -1560198380);
    b = md5ii(b,c,d,a, x[i+13], 21,  1309151649);
    a = md5ii(a,b,c,d, x[i+ 4], 6 , -145523070);
    d = md5ii(d,a,b,c, x[i+11], 10, -1120210379);
    c = md5ii(c,d,a,b, x[i+ 2], 15,  718787259);
    b = md5ii(b,c,d,a, x[i+ 9], 21, -343485551);
    a = safeAdd(a, olda);
    b = safeAdd(b, oldb);
    c = safeAdd(c, oldc);
    d = safeAdd(d, oldd);
  }
  return [a, b, c, d];
}

export function md5(str: string): string {
  const utf8 = unescape(encodeURIComponent(str));
  return binl2hex(binlMD5(str2binl(utf8), utf8.length * 8));
}

// ---------------------------------------------------------------------------
// Timestamp / checksum helpers (identical to the Worker)
// ---------------------------------------------------------------------------
export function pssTimestamp(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, '').replace('Z', '');
}

export function deviceLoginChecksum(deviceKey: string, timestamp: string): string {
  return md5(deviceKey + timestamp + DEVICE_TYPE + CHECKSUM_KEY);
}

export function emailPasswordChecksum(deviceKey: string, email: string, timestamp: string, accessToken: string): string {
  return md5(deviceKey + email + timestamp + accessToken + CHECKSUM_KEY);
}

// ---------------------------------------------------------------------------
// DeviceLogin17 path / body builders (identical to the Worker)
// ---------------------------------------------------------------------------
export function buildDeviceLoginPath(deviceKey: string, ts: string, checksum: string, refreshToken?: string | null): string {
  let p = '/UserService/DeviceLogin17' +
    '?AccessToken=00000000-0000-0000-0000-000000000000' +
    '&AdvertisingKey=' +
    '&Checksum=' + checksum +
    '&ClientDateTime=' + encodeURIComponent(ts) +
    '&DeviceKey=' + encodeURIComponent(deviceKey) +
    '&DeviceType=' + DEVICE_TYPE +
    '&IsJailBroken=false' +
    '&LanguageKey=en' +
    '&Signal=false';
  if (refreshToken) p += '&RefreshToken=' + encodeURIComponent(refreshToken);
  return p;
}

export function buildDeviceLoginBody(deviceKey: string, ts: string, checksum: string, refreshToken?: string | null): Record<string, unknown> {
  return {
    DeviceType:     DEVICE_TYPE,
    DeviceKey:      deviceKey,
    ClientDateTime: ts,
    Checksum:       checksum,
    IsJailBroken:   'false',
    Signal:         'false',
    LanguageKey:    'en',
    AdvertisingKey: '',
    AccessToken:    '00000000-0000-0000-0000-000000000000',
    RefreshToken:   refreshToken || '',
    UserDeviceInfo: {
      OsVersion:     'AD12',
      OSBuild:       '0',
      DeviceName:    'AD123',
      Locale:        'en',
      ClientBuild:   '17180',
      ClientVersion: '0.999.43'
    }
  };
}
